import React, { useState, useEffect, useCallback } from "react";
import {
	Table,
	Button,
	Space,
	Tag,
	Card,
	Row,
	Col,
	Modal,
	Select,
	DatePicker,
	Form,
	Badge,
	Typography,
	message,
} from "antd";
import { FilterOutlined, ReloadOutlined, CloseCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Papa from "papaparse";
import { ComponentTitle } from "../custom-components";
import { listStreams, listAsPlayed, fetchAllAsPlayed } from "./api";

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ET_ZONE = "America/New_York";

// Row shape after makeKeyInLowerCaseHelper lowercases the Snowflake columns of
// AS_PLAYED_LOG. season/episode are stored as strings; ended_at can be null for
// a play cut short by a stop.
interface AsPlayedEntry {
	play_id: string;
	stream_id: string;
	stream_name: string;
	video_id?: string | null;
	s3_key?: string | null;
	type: "content" | "ad";
	is_slate: boolean;
	series_title?: string | null;
	season?: string | null;
	episode?: string | null;
	episode_title?: string | null;
	started_at: string; // UTC ISO
	ended_at?: string | null; // UTC ISO
}

interface Stream {
	id: string;
	name: string;
}

interface Filters {
	stream_ids: string[];
	start_date: string | null;
	end_date: string | null;
	type: string | null;
}

interface PaginationState {
	current: number;
	pageSize: number;
	total: number;
}

const toET = (utcIso?: string | null): string =>
	utcIso ? dayjs.utc(utcIso).tz(ET_ZONE).format("MMM D, YYYY h:mm:ss A") : "—";

const durationLabel = (start?: string | null, end?: string | null): string => {
	if (!start || !end) return "—";
	const secs = dayjs(end).diff(dayjs(start), "second");
	if (!Number.isFinite(secs) || secs < 0) return "—";
	const m = Math.floor(secs / 60);
	const s = secs % 60;
	return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const typeColor: Record<string, string> = {
	content: "blue",
	ad: "gold",
};

// No default type filter — the report shows all types (content + ads/slates)
// until the user narrows it. Type is just another opt-in filter.
const DEFAULT_FILTERS: Filters = {
	stream_ids: [],
	start_date: null,
	end_date: null,
	type: null,
};

// Count a filter as active only when it differs from the default. Type is
// compared against DEFAULT_FILTERS.type (now null = all types), so selecting
// Content or Ad counts as active while the default does not — this keeps Clear
// honest (it resets to the default and the chip + Clear button disappear).
const activeFilterCount = (f: Filters): number =>
	(f.stream_ids.length > 0 ? 1 : 0) +
	(f.start_date || f.end_date ? 1 : 0) +
	(f.type !== DEFAULT_FILTERS.type ? 1 : 0);

const AsPlayedReporting: React.FC = () => {
	const [filterModalOpen, setFilterModalOpen] = useState(false);
	const [streams, setStreams] = useState<Stream[]>([]);
	const [entries, setEntries] = useState<AsPlayedEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);

	const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
	// Draft filters — only committed on "Apply".
	const [draftFilters, setDraftFilters] = useState<Filters>(DEFAULT_FILTERS);

	const [pagination, setPagination] = useState<PaginationState>({
		current: 1,
		pageSize: 25,
		total: 0,
	});

	// Load the stream list once for the filter multi-select + chip lookup.
	useEffect(() => {
		listStreams()
			.then(rows => setStreams(rows.map(s => ({ id: s.id, name: s.name }))))
			.catch(err => {
				console.error("Failed to load streams", err);
				message.error("Failed to load streams for filter");
			});
	}, []);

	// Server-side fetch. Re-runs when filters or the page/pageSize change.
	const fetchEntries = useCallback(async () => {
		setLoading(true);
		try {
			const { data, total } = await listAsPlayed({
				pagination: { current: pagination.current, pageSize: pagination.pageSize },
				sortObj: { column: "started_at", order: "desc" }, // newest first
				filter: {
					stream_ids: filters.stream_ids,
					start_date: filters.start_date,
					end_date: filters.end_date,
					type: filters.type,
				},
			});
			setEntries(data as AsPlayedEntry[]);
			setPagination(prev => ({ ...prev, total }));
		} catch (err) {
			console.error("Failed to load as-played log", err);
			message.error("Failed to load as-played log");
		} finally {
			setLoading(false);
		}
		// total is intentionally excluded — it's an output, not an input.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, pagination.current, pagination.pageSize]);

	useEffect(() => {
		fetchEntries();
	}, [fetchEntries]);

	const openFilterModal = () => {
		setDraftFilters(filters);
		setFilterModalOpen(true);
	};

	const applyFilters = () => {
		setFilters(draftFilters);
		setPagination(prev => ({ ...prev, current: 1 })); // new filter → back to page 1
		setFilterModalOpen(false);
	};

	// Patch filters and reset to page 1 (used by chip removal + clear).
	const patchFilters = (patch: Partial<Filters>) => {
		setFilters(f => ({ ...f, ...patch }));
		setPagination(prev => ({ ...prev, current: 1 }));
	};

	const clearFilters = () => {
		setFilters(DEFAULT_FILTERS);
		setDraftFilters(DEFAULT_FILTERS);
		setPagination(prev => ({ ...prev, current: 1 }));
	};

	// Client-side CSV export of every row matching the CURRENT filters (not just
	// the visible page). Pages the list endpoint, builds the CSV with the same
	// ET formatting the table uses, and downloads it in-browser — no backend
	// export endpoint. (Background/email export is deferred to a later ticket.)
	const handleExport = async () => {
		setExporting(true);
		const hideLoading = message.loading("Preparing export…", 0);
		try {
			const rows = (await fetchAllAsPlayed({
				stream_ids: filters.stream_ids,
				start_date: filters.start_date,
				end_date: filters.end_date,
				type: filters.type,
			})) as unknown as AsPlayedEntry[];

			if (rows.length === 0) {
				message.info("No plays match the current filters — nothing to export.");
				return;
			}

			// One column per field a user cares about, in the table's order. ET
			// times + human duration mirror the on-screen values; play_id is kept
			// as a stable reference.
			const csvRows = rows.map(r => ({
				"Started (ET)": toET(r.started_at),
				"Ended (ET)": toET(r.ended_at),
				Duration: durationLabel(r.started_at, r.ended_at),
				Stream: r.stream_name ?? "",
				Type: r.is_slate ? "Slate" : (r.type ?? "").toUpperCase(),
				Series: r.series_title ?? "",
				Season: r.season ?? "",
				Episode: r.episode ?? "",
				"Episode Title": r.episode_title ?? "",
				"Video / File": r.video_id || r.s3_key || "",
				"Play ID": r.play_id,
			}));

			const csv = Papa.unparse(csvRows);
			const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `as-played-${dayjs().tz(ET_ZONE).format("YYYY-MM-DD_HHmmss")}.csv`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			message.success(`Exported ${rows.length.toLocaleString()} play${rows.length === 1 ? "" : "s"}.`);
		} catch (err) {
			console.error("Failed to export as-played log", err);
			message.error("Failed to export as-played log");
		} finally {
			hideLoading();
			setExporting(false);
		}
	};

	const filterCount = activeFilterCount(filters);

	const columns = [
		{
			title: "Started (ET)",
			dataIndex: "started_at",
			key: "started_at",
			width: 200,
			render: (v: string) => <Text style={{ fontSize: 13 }}>{toET(v)}</Text>,
		},
		{
			title: "Ended (ET)",
			dataIndex: "ended_at",
			key: "ended_at",
			width: 200,
			render: (v?: string | null) => <Text style={{ fontSize: 13 }}>{toET(v)}</Text>,
		},
		{
			title: "Duration",
			key: "duration",
			width: 90,
			render: (_: any, r: AsPlayedEntry) => (
				<Text type="secondary" style={{ fontSize: 13 }}>
					{durationLabel(r.started_at, r.ended_at)}
				</Text>
			),
		},
		{
			title: "Stream",
			dataIndex: "stream_name",
			key: "stream_name",
			width: 160,
			render: (v: string) => <Tag>{v}</Tag>,
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 90,
			render: (v: string, r: AsPlayedEntry) => (
				<Tag color={r.is_slate ? "default" : typeColor[v] ?? "default"}>
					{r.is_slate ? "Slate" : v?.toUpperCase()}
				</Tag>
			),
		},
		{
			title: "Series",
			dataIndex: "series_title",
			key: "series_title",
			render: (v?: string | null) => (v ? <Text>{v}</Text> : <Text type="secondary">—</Text>),
		},
		{
			title: "Season / Ep",
			key: "season_episode",
			width: 110,
			render: (_: any, r: AsPlayedEntry) => {
				const parts = [
					r.season != null && r.season !== "" ? `S${String(r.season).padStart(2, "0")}` : null,
					r.episode != null && r.episode !== "" ? `E${String(r.episode).padStart(2, "0")}` : null,
				].filter(Boolean);
				return parts.length ? (
					<Text style={{ fontVariantNumeric: "tabular-nums" }}>{parts.join("")}</Text>
				) : (
					<Text type="secondary">—</Text>
				);
			},
		},
		{
			title: "Episode Title",
			dataIndex: "episode_title",
			key: "episode_title",
			render: (v?: string | null) => (v ? <Text>{v}</Text> : <Text type="secondary">—</Text>),
		},
		{
			title: "Video / File",
			key: "video_ref",
			render: (_: any, r: AsPlayedEntry) => {
				const ref = r.video_id || r.s3_key || "—";
				return (
					<Text
						code
						style={{
							fontSize: 11,
							maxWidth: 220,
							display: "inline-block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
						title={ref}
					>
						{ref}
					</Text>
				);
			},
		},
	];

	return (
		<div style={{ padding: "24px" }}>
			{/* Header */}
			<Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
				<Col>
					<ComponentTitle>As Played Reporting</ComponentTitle>
				</Col>
				<Col>
					<Space>
						<Badge count={filterCount} size="small">
							<Button icon={<FilterOutlined />} onClick={openFilterModal}>
								Filters{filterCount > 0 ? ` (${filterCount})` : ""}
							</Button>
						</Badge>
						{filterCount > 0 && (
							<Button type="text" danger icon={<CloseCircleOutlined />} onClick={clearFilters}>
								Clear
							</Button>
						)}
						<Button
							icon={<DownloadOutlined />}
							onClick={handleExport}
							loading={exporting}
							disabled={loading}
						>
							Export CSV
						</Button>
						<Button icon={<ReloadOutlined />} onClick={fetchEntries} loading={loading}>
							Refresh
						</Button>
					</Space>
				</Col>
			</Row>

			{/* Active filter chips */}
			{filterCount > 0 && (
				<Row style={{ marginBottom: 16 }}>
					<Col>
						<Space wrap>
							{filters.stream_ids.length > 0 && (
								<Tag closable onClose={() => patchFilters({ stream_ids: [] })}>
									Streams:{" "}
									{filters.stream_ids.length === 1
										? streams.find(s => s.id === filters.stream_ids[0])?.name ??
										  filters.stream_ids[0]
										: `${filters.stream_ids.length} selected`}
								</Tag>
							)}
							{(filters.start_date || filters.end_date) && (
								<Tag closable onClose={() => patchFilters({ start_date: null, end_date: null })}>
									{filters.start_date ? dayjs(filters.start_date).tz(ET_ZONE).format("MMM D") : "…"}
									{" – "}
									{filters.end_date ? dayjs(filters.end_date).tz(ET_ZONE).format("MMM D") : "…"}
								</Tag>
							)}
							{filters.type !== DEFAULT_FILTERS.type && (
								<Tag closable onClose={() => patchFilters({ type: DEFAULT_FILTERS.type })}>
									Type: {filters.type ?? "all"}
								</Tag>
							)}
						</Space>
					</Col>
				</Row>
			)}

			{/* Table (server-side pagination) */}
			<Card>
				<Table
					columns={columns}
					dataSource={entries}
					rowKey="play_id"
					size="small"
					loading={loading}
					scroll={{ x: 1200 }}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						pageSizeOptions: ["10", "25", "50", "100"],
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}–${range[1]} of ${total.toLocaleString()} plays`,
						onChange: (page, pageSize) => {
							setPagination(prev => ({
								...prev,
								current: page,
								pageSize: pageSize ?? prev.pageSize,
							}));
						},
					}}
				/>
			</Card>

			{/* Filter Modal */}
			<Modal
				title="Filter As Played Log"
				open={filterModalOpen}
				onCancel={() => setFilterModalOpen(false)}
				onOk={applyFilters}
				okText="Apply Filters"
				destroyOnClose
				width={480}
			>
				<Form layout="vertical" style={{ marginTop: 16 }}>
					<Form.Item label="Streams">
						<Select
							mode="multiple"
							allowClear
							placeholder="All streams"
							value={draftFilters.stream_ids}
							onChange={v => setDraftFilters(f => ({ ...f, stream_ids: v }))}
							options={streams.map(s => ({ label: s.name, value: s.id }))}
							style={{ width: "100%" }}
							optionFilterProp="label"
							showSearch
						/>
					</Form.Item>

					<Form.Item label="Date Range (ET)">
						<RangePicker
							style={{ width: "100%" }}
							value={[
								draftFilters.start_date ? dayjs(draftFilters.start_date).tz(ET_ZONE) : null,
								draftFilters.end_date ? dayjs(draftFilters.end_date).tz(ET_ZONE) : null,
							]}
							onChange={dates => {
								setDraftFilters(f => ({
									...f,
									// Cover the full ET days the user picked, converted to UTC for
									// the backend's started_at range compare.
									start_date: dates?.[0]
										? dates[0].tz(ET_ZONE).startOf("day").utc().toISOString()
										: null,
									end_date: dates?.[1]
										? dates[1].tz(ET_ZONE).endOf("day").utc().toISOString()
										: null,
								}));
							}}
						/>
					</Form.Item>

					<Form.Item label="Type">
						<Select
							allowClear
							placeholder="All types"
							value={draftFilters.type}
							onChange={v => setDraftFilters(f => ({ ...f, type: v ?? null }))}
							options={[
								{ label: "Content", value: "content" },
								{ label: "Ad", value: "ad" },
							]}
							style={{ width: "100%" }}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default AsPlayedReporting;
